import React, { useState, useEffect } from 'react';
import { engineerService } from '../../services/engineerService';
import { uploadRecallPhoto, isDataUrl } from '../../services/photoService';

const RecallAction = ({ execId, vehicle, initialData, onSave, onCancel }) => {
  const [actionDesc, setActionDesc] = useState(initialData?.actionDesc || '');
  const [photos, setPhotos] = useState(initialData?.photos || []);
  const [engineers, setEngineers] = useState([]); 
  const [saving, setSaving] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState(initialData?.engineer || '');
  
  const getCurrentDateTime = () => {
    const tzOffset = new Date().getTimezoneOffset() * 60000; 
    return new Date(Date.now() - tzOffset).toISOString().slice(0, 16);
  };
  const [actionDateTime, setActionDateTime] = useState(initialData?.actionDateTime || getCurrentDateTime());

  useEffect(() => {
    const fetchEngineers = async () => {
      const data = await engineerService.getEngineers();
      setEngineers(data || []);
    };
    fetchEngineers();
  }, []);

  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          resolve(dataUrl);
        };
      };
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) {
      return alert("사진은 최대 5장까지만 등록 가능합니다.");
    }
    const compressedPhotos = [];
    for (const file of files) {
      const resized = await resizeImage(file);
      compressedPhotos.push(resized);
    }
    setPhotos(prev => [...prev, ...compressedPhotos]);
  };

  const submitAction = async () => {
    if (!actionDesc) return alert("조치 내역을 입력해주세요.");
    if (!selectedEngineer) return alert("담당 엔지니어를 선택해주세요.");
    if (saving) return;

    setSaving(true);
    try {
      // 새로 고른 사진만 파일 보관소(Storage)에 올리고, 문서에는 짧은 주소만 남긴다.
      // 사진을 문서에 직접 넣으면 1MB 한도를 넘겨 저장이 통째로 실패한다.
      const uploaded = [];
      for (const src of photos) {
        uploaded.push(isDataUrl(src) ? await uploadRecallPhoto(execId, vehicle.carNumber, src) : src);
      }

      await onSave(execId, vehicle.carNumber, {
        actionDesc,
        photos: uploaded,
        engineer: selectedEngineer,
        actionDateTime
      });
    } catch (err) {
      console.error('리콜 조치 저장 실패:', err);
      // 업로드가 실패했는데 저장했다고 알리면 사진이 사라진 줄 모르게 되므로 분명히 알린다.
      alert("사진 업로드에 실패하여 저장하지 않았습니다.\n네트워크를 확인하고 다시 시도해 주세요.");
    }
    setSaving(false);
  };

  const style = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 },
    modal: { background: 'white', width: '600px', padding: '40px', borderRadius: '24px', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' },
    infoBox: { background: '#f8fafc', padding: '20px', borderRadius: '15px', marginBottom: '25px', border: '1px solid #e2e8f0' },
    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginTop: '15px' },
    input: { width: '100%', padding: '14px 18px', border: '1px solid #e2e8f0', borderRadius: '12px', marginTop: '8px', fontSize: '14px', boxSizing: 'border-box' },
    label: { fontSize: '13px', fontWeight: 'bold', color: '#64748b' }
  };

  return (
    <div style={style.overlay}>
      <div style={style.modal}>
        <h3 style={{ marginTop: 0 }}>🛠️ 리콜 조치 기록 {initialData ? '(수정)' : ''}</h3>
        <div style={style.infoBox}>
          <div style={{ fontSize: '18px', fontWeight: '800' }}>{vehicle.carNumber}</div>
          <div style={{ fontSize: '14px', marginTop: '5px', color: '#1e293b' }}>고객명: {vehicle.customerName}</div>
        </div>
        <label style={style.label}>현장 조치 내역</label>
        <textarea style={{ ...style.input, height: '100px', resize: 'none' }} value={actionDesc} onChange={e => setActionDesc(e.target.value)} />
        <div style={{ marginTop: '20px' }}>
          <label style={style.label}>현장 사진 ({photos.length}/5)</label>
          <input type="file" multiple onChange={handlePhotoUpload} style={{ display: 'block', marginTop: '10px' }} />
          <div style={style.photoGrid}>
            {photos.map((src, i) => (
              <div key={i} style={{ height: '80px', background: `url(${src}) center/cover`, borderRadius: '8px', position: 'relative' }}>
                <span onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))} style={{position:'absolute', top:'-5px', right:'-5px', background:'red', color:'white', borderRadius:'50%', width:'18px', height:'18px', textAlign:'center', fontSize:'12px', cursor:'pointer'}}>x</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '25px' }}>
          <div>
            <label style={style.label}>담당 엔지니어</label>
            <select value={selectedEngineer} onChange={e => setSelectedEngineer(e.target.value)} style={style.input}>
              <option value="">선택</option>
              {engineers.map(eng => <option key={eng.id} value={eng.name}>{eng.name}</option>)}
            </select>
          </div>
          <div>
            <label style={style.label}>작업 일시</label>
            <input type="datetime-local" value={actionDateTime} onChange={e => setActionDateTime(e.target.value)} style={style.input} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '15px', marginTop: '30px' }}>
          <button onClick={submitAction} disabled={saving} style={{ flex: 1, padding: '15px', background: saving ? '#94a3b8' : '#1e293b', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: saving ? 'default' : 'pointer' }}>{saving ? '사진 업로드 중...' : '저장하기'}</button>
          <button onClick={onCancel} style={{ flex: 0.4, padding: '15px', background: '#f1f5f9', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>취소</button>
        </div>
      </div>
    </div>
  );
};

export default RecallAction;